import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Search, SlidersHorizontal, Star, ChevronDown } from "lucide-react";
import { api, teamBadgeUrl } from "@/lib/api";
import { useBootstrap } from "@/lib/useMe";
import PlayerAvatar from "@/components/app/PlayerAvatar";
import FDRPill from "@/components/app/FDRPill";
import { T } from "@/lib/testIds";
import { cn } from "@/lib/utils";

const SORTS = [
  { key: "total_points", label: "Total Pts" },
  { key: "form", label: "Form" },
  { key: "ict_index", label: "ICT" },
  { key: "expected_goals", label: "xG" },
  { key: "expected_assists", label: "xA" },
  { key: "points_per_game", label: "PPG" },
  { key: "selected_by_percent", label: "TSB %" },
  { key: "now_cost", label: "Price" },
];

export default function Players() {
  const [q, setQ] = useState("");
  const [position, setPosition] = useState("");
  const [teamId, setTeamId] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [sort, setSort] = useState("total_points");
  const [showFilters, setShowFilters] = useState(false);

  const { data: boot } = useBootstrap();

  const { data, isLoading } = useQuery({
    queryKey: ["players", { q, position, teamId, maxPrice, sort }],
    queryFn: async () => {
      const params = { sort, limit: 60 };
      if (q) params.q = q;
      if (position) params.position = Number(position);
      if (teamId) params.team_id = Number(teamId);
      if (maxPrice) params.max_price = Number(maxPrice);
      return (await api.get("/players", { params })).data;
    },
    staleTime: 60_000,
  });

  const positions = boot?.positions || [];
  const teams = boot?.teams || [];

  return (
    <div data-testid={T.playersPage} className="space-y-3 animate-fade-up">
      {/* Search */}
      <div className="relative">
        <Search
          className="absolute left-3 top-1/2 -translate-y-1/2 text-totem-textDim"
          size={16}
        />
        <input
          data-testid={T.playersSearch}
          placeholder="Search players by name…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="w-full bg-totem-surfaceElev border border-totem-border rounded-lg pl-10 pr-3 py-3 focus:outline-none focus:border-totem-green/60 transition"
        />
      </div>

      <button
        type="button"
        onClick={() => setShowFilters((v) => !v)}
        className="w-full flex items-center justify-center gap-2 bg-totem-surfaceElev border border-totem-border rounded-lg py-2.5 mono uppercase text-xs tracking-wider hover:border-totem-green/40 transition"
      >
        <SlidersHorizontal size={14} /> Filters
        <ChevronDown size={14} className={cn("transition-transform", showFilters && "rotate-180")} />
      </button>

      {showFilters && (
        <div className="grid grid-cols-2 gap-2 animate-fade-up">
          <select
            data-testid={T.playersPositionFilter}
            value={position}
            onChange={(e) => setPosition(e.target.value)}
            className="bg-totem-surfaceElev border border-totem-border rounded-lg px-3 py-2.5 mono uppercase text-xs"
          >
            <option value="">All positions</option>
            {positions.map((p) => (
              <option key={p.id} value={p.id}>{p.singular_name}</option>
            ))}
          </select>
          <input
            data-testid={T.playersMaxPriceFilter}
            placeholder="Max price (£m)"
            type="number"
            step="0.1"
            value={maxPrice}
            onChange={(e) => setMaxPrice(e.target.value)}
            className="bg-totem-surfaceElev border border-totem-border rounded-lg px-3 py-2.5 mono text-xs"
          />
          <select
            data-testid={T.playersTeamFilter}
            value={teamId}
            onChange={(e) => setTeamId(e.target.value)}
            className="bg-totem-surfaceElev border border-totem-border rounded-lg px-3 py-2.5 mono uppercase text-xs col-span-2"
          >
            <option value="">All teams</option>
            {teams.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>
      )}

      {/* Sort chips */}
      <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4">
        {SORTS.map((s) => (
          <button
            key={s.key}
            data-testid={s.key === sort ? T.playersSortSelect : undefined}
            onClick={() => setSort(s.key)}
            className={cn(
              "shrink-0 rounded-full px-3 py-1.5 mono text-[11px] uppercase tracking-wider border transition",
              sort === s.key
                ? "bg-totem-green/15 text-totem-green border-totem-green/60"
                : "bg-totem-surfaceElev border-totem-border text-totem-textDim hover:border-totem-textDim"
            )}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* List */}
      <ul data-testid={T.playersList} className="space-y-2">
        {isLoading &&
          Array.from({ length: 6 }).map((_, i) => (
            <li key={i} className="totem-card h-24 animate-pulse" />
          ))}
        {data?.results?.map((p) => (
          <li key={p.id} data-testid={T.playersRow}>
            <Link
              to={`/players/${p.id}`}
              className="totem-card p-4 flex items-center gap-3 hover:border-totem-green/40 transition"
            >
              <PlayerAvatar player={p} size={44} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <div className="font-semibold truncate">{p.web_name}</div>
                  {p.selected_by_percent < 5 && (
                    <span className="mono text-[9px] uppercase bg-totem-gold/15 text-totem-gold border border-totem-gold/30 rounded px-1.5 py-0.5">
                      Diff
                    </span>
                  )}
                </div>
                <div className="mono text-[10px] uppercase text-totem-textDim mt-0.5">
                  {p.team_short} • {p.position}
                </div>
                <div className="flex items-center gap-3 mt-2 text-xs mono">
                  <span className="text-totem-textDim">
                    £<span className="text-totem-text">{p.now_cost.toFixed(1)}m</span>
                  </span>
                  <span className="text-totem-textDim">
                    Form <span className="text-totem-green">{p.form}</span>
                  </span>
                  <span className="text-totem-textDim">
                    TSB <span className="text-totem-text">{p.selected_by_percent}%</span>
                  </span>
                </div>
              </div>
              <div className="text-right">
                <div className="display-heading text-xl text-totem-green">{p.total_points}</div>
                <div className="mono text-[9px] uppercase text-totem-textDim">pts</div>
              </div>
            </Link>
          </li>
        ))}
      </ul>

      {data && data.count === 0 && (
        <div className="totem-card p-6 text-center text-totem-textDim text-sm">
          No players match those filters.
        </div>
      )}
    </div>
  );
}
